/**
 * Call Automation Service
 * Handles data fetching for smart fields in call automation tables
 */

import { getFacilities, getProviders, getDepartments } from '../utils/apiClient';

class CallAutomationService {
  constructor() {
    this.facilitiesCache = null;
    this.providersCache = null;
    this.departmentsCache = null;
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    this.facilitiesCacheTime = null;
    this.providersCacheTime = null;
    this.departmentsCacheTime = null;
  }

  /**
   * Gets all facilities with caching
   * @returns {Promise<Array>} Array of facility objects
   */
  async getFacilities() {
    try {
      // Check if cache is valid
      if (this.facilitiesCache && this.facilitiesCacheTime && 
          (Date.now() - this.facilitiesCacheTime) < this.cacheExpiry) {
        console.log('📋 Using cached facilities data');
        return this.facilitiesCache;
      }

      console.log('🔄 Fetching facilities from API...');
      const response = await getFacilities();
      
      // Extract facilities from response structure
      let facilities = [];
      if (response && typeof response === 'object') {
        // Handle both array and object response structures
        if (Array.isArray(response)) {
          facilities = response;
        } else if (response.response && Array.isArray(response.response)) {
          facilities = response.response;
        } else {
          // Handle numbered object structure like {"0": {...}, "1": {...}}
          facilities = Object.values(response).filter(item => 
            item && typeof item === 'object' && item._id
          );
        }
      }

      // Transform facilities for dropdown use
      const transformedFacilities = facilities.map(facility => ({
        id: facility._id,
        label: `${facility.facilityName} - NPI: ${facility.groupNPI}`,
        value: facility.groupNPI,
        facilityName: facility.facilityName,
        location: facility.location,
        groupNPI: facility.groupNPI,
        phoneNumber: facility.phoneNumber,
        isActive: facility.isActive,
        // Add formatted address for better identification
        formattedAddress: facility.location ? 
          `${facility.location.streetAddress || ''} ${facility.location.city || ''} ${facility.location.state || ''} ${facility.location.zipcode || ''}`.trim() : 'No address'
      })).filter(facility => facility.isActive && facility.value); // Only active facilities with NPI
      
      // Update labels for facilities with duplicate NPIs to include address
      const npiCounts = {};
      transformedFacilities.forEach(facility => {
        npiCounts[facility.groupNPI] = (npiCounts[facility.groupNPI] || 0) + 1;
      });
      
      // Enhance labels for duplicate NPIs
      transformedFacilities.forEach(facility => {
        if (npiCounts[facility.groupNPI] > 1) {
          facility.label = `${facility.facilityName} - NPI: ${facility.groupNPI} | ${facility.formattedAddress}`;
          console.log(`🏥 Enhanced label for duplicate NPI ${facility.groupNPI}: ${facility.label}`);
        }
      });

      // Update cache
      this.facilitiesCache = transformedFacilities;
      this.facilitiesCacheTime = Date.now();

      console.log(`✅ Loaded ${transformedFacilities.length} facilities`);
      return transformedFacilities;
    } catch (error) {
      console.error('❌ Error fetching facilities:', error);
      return this.facilitiesCache || []; // Return cached data or empty array on error
    }
  }

  /**
   * Gets all rendering providers with caching  
   * @returns {Promise<Array>} Array of provider objects
   */
  async getRenderingProviders() {
    try {
      // Check if cache is valid
      if (this.providersCache && this.providersCacheTime && 
          (Date.now() - this.providersCacheTime) < this.cacheExpiry) {
        console.log('👨‍⚕️ Using cached providers data');
        return this.providersCache;
      }

      console.log('🔄 Fetching rendering providers from API...');
      const response = await getProviders();

      // Extract providers from response structure
      let providers = [];
      if (response && typeof response === 'object') {
        // Handle both array and object response structures
        if (Array.isArray(response)) {
          providers = response;
        } else if (response.response && Array.isArray(response.response)) {
          providers = response.response;
        } else {
          // Handle numbered object structure like {"0": {...}, "1": {...}}
          providers = Object.values(response).filter(item => 
            item && typeof item === 'object' && item._id
          );
        }
      }

      // Transform providers for dropdown use
      const transformedProviders = providers.map(provider => ({
        id: provider._id,
        label: `${provider.fullName} (NPI: ${provider.npi})`,
        value: provider.npi,
        fullName: provider.fullName,
        npi: provider.npi,
        specialties: provider.specialties,
        affiliations: provider.affiliations,
        isActive: provider.isActive
      })).filter(provider => provider.isActive && provider.value); // Only active providers with NPI

      // Update cache
      this.providersCache = transformedProviders;
      this.providersCacheTime = Date.now();

      console.log(`✅ Loaded ${transformedProviders.length} rendering providers`);
      return transformedProviders;
    } catch (error) {
      console.error('❌ Error fetching rendering providers:', error);
      return this.providersCache || []; // Return cached data or empty array on error
    }
  }

  /**
   * Gets all departments with caching
   * @returns {Promise<Array>} Array of department objects
   */
  async getDepartments() {
    try {
      // Check if cache is valid
      if (this.departmentsCache && this.departmentsCacheTime && 
          (Date.now() - this.departmentsCacheTime) < this.cacheExpiry) {
        console.log('🏢 Using cached departments data');
        return this.departmentsCache;
      }

      console.log('🔄 Fetching departments from API...');
      const response = await getDepartments();

      // Extract departments from response structure
      let departments = [];
      if (response && typeof response === 'object') {
        // Handle both array and object response structures
        if (Array.isArray(response)) {
          departments = response;
        } else if (response.response && Array.isArray(response.response)) {
          departments = response.response;
        } else {
          // Handle numbered object structure like {"0": {...}, "1": {...}}
          departments = Object.values(response).filter(item => 
            item && typeof item === 'object' && item._id
          );
        }
      }

      // Transform departments for dropdown use (no transformation needed, use name as both label and value)
      const allDepartments = departments.map(department => ({
        id: department._id,
        label: department.name,
        value: department.name,
        name: department.name,
        facilityId: department.facilityId,
        faxNumber: department.faxNumber,
        isActive: department.isActive
      }));
      
      // Filter to only include active departments with a name
      const transformedDepartments = allDepartments.filter(department => department.isActive === true && department.value);
      
      const inactiveCount = allDepartments.length - transformedDepartments.length;
      console.log(`✅ Loaded ${transformedDepartments.length} active departments (filtered out ${inactiveCount} inactive)`);

      // Update cache
      this.departmentsCache = transformedDepartments;
      this.departmentsCacheTime = Date.now();
      return transformedDepartments;
    } catch (error) {
      console.error('❌ Error fetching departments:', error);
      return this.departmentsCache || []; // Return cached data or empty array on error
    }
  }

  /**
   * Searches facilities by name or NPI
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} Filtered facilities
   */
  async searchFacilities(searchTerm) {
    const facilities = await this.getFacilities();
    if (!searchTerm) return facilities;

    const term = searchTerm.toLowerCase();
    return facilities.filter(facility => 
      facility.facilityName.toLowerCase().includes(term) ||
      facility.groupNPI.includes(term) ||
      facility.location?.city?.toLowerCase().includes(term)
    );
  }

  /**
   * Searches providers by name or NPI
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} Filtered providers
   */
  async searchProviders(searchTerm) {
    const providers = await this.getRenderingProviders();
    if (!searchTerm) return providers;

    const term = searchTerm.toLowerCase();
    return providers.filter(provider => 
      provider.fullName.toLowerCase().includes(term) ||
      provider.npi.includes(term) ||
      provider.specialties?.some(specialty => specialty.toLowerCase().includes(term))
    );
  }

  /**
   * Searches departments by name
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} Filtered departments
   */
  async searchDepartments(searchTerm) {
    const departments = await this.getDepartments();
    if (!searchTerm) return departments;

    const term = searchTerm.toLowerCase();
    return departments.filter(department => 
      department.name.toLowerCase().includes(term)
    );
  }

  /**
   * Clears all caches
   */
  clearCache() {
    this.facilitiesCache = null;
    this.providersCache = null;
    this.departmentsCache = null;
    this.facilitiesCacheTime = null;
    this.providersCacheTime = null;
    this.departmentsCacheTime = null;
    console.log('🗑️ Cleared call automation service cache');
  }
}

// Create singleton instance
const callAutomationServiceInstance = new CallAutomationService();

// Export singleton instance
export default callAutomationServiceInstance;
export { callAutomationServiceInstance as callAutomationService };
